import { useState } from 'react';
import { transferOwnership } from '../../api/clubs';
import type { ClubMemberResponse } from '../../types/api';

interface Props {
  clubId: number;
  members: ClubMemberResponse[];
  currentOwnerUserId: string;
  open: boolean;
  onClose: () => void;
  onTransferred: () => void;
}

export function TransferOwnershipDialog({
  clubId,
  members,
  currentOwnerUserId,
  open,
  onClose,
  onTransferred,
}: Props) {
  const [selected, setSelected] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const eligible = members.filter(
    (m) => m.userId !== currentOwnerUserId && m.role !== 'Guest'
  );

  async function handleTransfer() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await transferOwnership(clubId, selected);
      onTransferred();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Transfer ownership
        </h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
          You will become an Admin after transferring. This cannot be undone without the new
          Owner's cooperation.
        </p>

        {eligible.length === 0 ? (
          <p className="mb-4 text-sm text-amber-600 dark:text-amber-400">
            No eligible members. Promote a Guest to Member or invite someone first.
          </p>
        ) : (
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={busy}
            className="mb-4 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
          >
            <option value="">Select new owner…</option>
            {eligible.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName} ({m.role})
              </option>
            ))}
          </select>
        )}

        {error && <p className="mb-2 text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={busy || !selected || eligible.length === 0}
            className="rounded-md bg-amber-600 hover:bg-amber-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Transferring…' : 'Transfer ownership'}
          </button>
        </div>
      </div>
    </div>
  );
}
